// Moteur local de résistance à la devinette (zxcvbn-ts). 100 % navigateur, aucun appel réseau.
// Dictionnaires : common + English + langue active (si un pack existe), résolus via le registre des langues.
import { getLocaleConfig } from "../../i18n/locales";
import { ZXCVBN_PACKS } from "./zxcvbnPacks";

export const MAX_ANALYZED_LENGTH = 256; // au-delà, zxcvbn n'analyse que les 256 premiers caractères
export const SERVICE_TERMS = ["securepass", "secure", "pass"]; // termes publics du service, toujours pénalisés

const cache = {};

// Traductions « identité » : zxcvbn renvoie des CLÉS (warnings.x / suggestions.y) que nous traduisons via i18n.
const keyTranslations = (tr) => Object.fromEntries(
  Object.entries(tr).map(([group, keys]) => [group, Object.fromEntries(Object.keys(keys).map((k) => [k, `${group}.${k}`]))]),
);

export async function loadEngine(lang) {
  const { wordlistLocale } = getLocaleConfig(lang);
  const code = ZXCVBN_PACKS[wordlistLocale] ? wordlistLocale : "en";
  if (cache[code]) return cache[code];
  cache[code] = (async () => {
    const codes = Array.from(new Set(["en", code]));
    const [core, common, ...packs] = await Promise.all([
      import("@zxcvbn-ts/core"),
      import("@zxcvbn-ts/language-common"),
      ...codes.map((c) => ZXCVBN_PACKS[c]()),
    ]);
    const dictionary = Object.assign({}, common.dictionary, ...packs.map((p) => p.dictionary));
    const factory = new core.ZxcvbnFactory({
      dictionary,
      graphs: common.adjacencyGraphs,
      translations: keyTranslations(packs[0].translations),
      useLevenshteinDistance: true,
      maxLength: MAX_ANALYZED_LENGTH,
    });
    return { factory, dictionaries: codes };
  })();
  return cache[code];
}

// Contexte utilisateur facultatif (jamais stocké / transmis) : découpé en jetons utiles à zxcvbn.
export function contextTokens(ctx = {}) {
  const out = new Set(SERVICE_TERMS);
  Object.values(ctx).forEach((v) => {
    const s = String(v || "").trim().toLowerCase();
    if (!s) return;
    out.add(s);
    s.split(/[^\p{L}\p{N}]+/u).filter((x) => x.length >= 3).forEach((x) => out.add(x));
  });
  return Array.from(out);
}

export async function analyzePassword(password, ctx, lang) {
  const { factory, dictionaries } = await loadEngine(lang);
  const r = factory.check(password, contextTokens(ctx));
  return {
    score: r.score,
    guesses: r.guesses,
    guessesLog10: r.guessesLog10,
    sequence: r.sequence,
    crackSeconds: {
      online: r.crackTimes.onlineThrottlingXPerHour.seconds,
      onlineFast: r.crackTimes.onlineNoThrottlingXPerSecond.seconds,
      offlineSlow: r.crackTimes.offlineSlowHashingXPerSecond.seconds,
      offlineFast: r.crackTimes.offlineFastHashingXPerSecond.seconds,
    },
    warning: r.feedback.warning || null,
    suggestions: r.feedback.suggestions || [],
    dictionaries,
    truncated: password.length > MAX_ANALYZED_LENGTH,
  };
}

// Durée -> clé de libellé (traduite par l'UI). Pas de fausse précision.
export function durationBucket(seconds) {
  const s = Number(seconds);
  const steps = [[1, "ltSecond"], [60, "seconds"], [3600, "minutes"], [86400, "hours"], [2592000, "days"], [31536000, "months"], [3153600000, "years"]];
  for (const [limit, key] of steps) if (s < limit) return key;
  return "centuries";
}

// Ordre de grandeur des devinettes -> clé pédagogique.
export function guessesBucket(log10) {
  if (log10 < 4) return "thousands";
  if (log10 < 7) return "millions";
  if (log10 < 10) return "billions";
  if (log10 < 14) return "trillions";
  return "astronomical";
}
